import { useLazyLoadQuery } from 'react-relay';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { BylineContentType } from '../utils/contentAri.ts';

import RelatedContentSampleQuery, {
	type preloadRelatedContentSampleQuery,
	type preloadRelatedContentSampleQuery$variables,
} from './__generated__/preloadRelatedContentSampleQuery.graphql.ts';
import {
	buildRelatedContentSampleVariables,
	normalizeRelatedContentSsrState,
	type RelatedContentSsrState,
} from './relatedContentSsrState.ts';

// Read with these only when there is nothing to look up, so the exact value never keys a real store
// read. A store-only read never touches the network, so a dummy lookup is free.
const SKIPPED_VARIABLES: preloadRelatedContentSampleQuery$variables = {
	cypherQuery: '',
	queryContext: '',
	version: 'V3',
	first: 0,
};

// Reads the exact bounded sample the related-content preload warmed, from the Relay store, without
// ever issuing a network request (`store-only`), the same way `useBylinePageAdf` reads its preloaded
// document. State is derived purely from the hydrated store, so it is identical on the server and
// the client's first render. A store miss (no preload, invalid input, or a non-view route) leaves the
// field absent and contributes no rows.
export const useRelatedContentSsrState = (
	contentId: string | undefined,
	contentType?: BylineContentType,
): RelatedContentSsrState | undefined => {
	const { cloudId, activationId } = useSessionData();
	const variables =
		!contentId || !cloudId || !activationId
			? undefined
			: buildRelatedContentSampleVariables(cloudId, activationId, contentId, contentType);

	const data = useLazyLoadQuery<preloadRelatedContentSampleQuery>(
		RelatedContentSampleQuery,
		variables ?? SKIPPED_VARIABLES,
		{ fetchPolicy: 'store-only' },
	);

	if (!contentId || variables === undefined || data?.graphStore_cypherQueryV2 == null) {
		return undefined;
	}

	return normalizeRelatedContentSsrState(contentId, data);
};
